import { ReactElement, useEffect, useRef, useState } from "react";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import { Tooltip, TooltipPosition, TooltipType } from "@/app/(components)/generics/Tootlip/Tooltip";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { PageParams } from "@/app/types/common/DefaultProps";
import { URLs } from "@/app/utils/URLs";
import { UStyles } from "@/app/utils/UStyles";
import { Limits } from "@/app/vars/Limits";
import styles from "../Sidebar.module.css";

type Props = {
  params: PageParams;
  lang: string;
  label: TKey;
  href?: string;
  icon?: ReactElement;
  isActive: boolean;
  isLogged: boolean;
  isOpen: boolean;
  onNavigate: () => void;
};

export default function SidebarNavItem(props: Props) {
  const T = useT();
  const { params, label, href, icon, isActive, isLogged, isOpen, onNavigate } = props;

  const [isWanted, setIsWanted] = useState(false);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const linkHref = href ? URLs.pathOf(href) : href;
  const className = UStyles.classNames(styles.navButton, isActive ? styles.activeItem : "");
  const prefetch = isLogged ? isWanted || undefined : true;

  const cancelHover = () => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  };

  const startHover = () => {
    if (isWanted) {
      return;
    }
    cancelHover();
    hoverTimer.current = setTimeout(() => setIsWanted(true), Limits.HOVER_PREFETCH_DELAY);
  };

  useEffect(() => {
    return () => {
      if (hoverTimer.current) {
        clearTimeout(hoverTimer.current);
      }
    };
  }, []);

  return (
    <div
      className={styles.navItem}
      onMouseEnter={startHover}
      onMouseLeave={cancelHover}
      onFocus={() => setIsWanted(true)}
    >
      {isOpen ? (
        <Button
          min={false}
          params={params}
          text={T(label)}
          href={linkHref}
          isLink={true}
          prefetch={prefetch}
          type={ButtonType.GHOST}
          full={true}
          beforeIcon={icon}
          className={className}
          onClick={onNavigate}
        />
      ) : (
        <Tooltip
          position={TooltipPosition.RIGHT}
          gap={Limits.SIDEBAR_TOOLTIP_GAP}
          type={TooltipType.GREEN}
          content={T(label)}
          className={styles.fullWidth}
          trigger={
            <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
              <Button
                min={false}
                params={params}
                href={linkHref}
                isLink={true}
                prefetch={prefetch}
                type={ButtonType.GHOST}
                iconOnly={true}
                beforeIcon={icon}
                full={false}
                className={className}
                ariaLabel={T(label)}
                onClick={onNavigate}
              />
            </div>
          }
        />
      )}
    </div>
  );
}
